import { observer } from "mobx-react-lite";
import { css } from "@/domains/emotion";
import { useAppStore, useGuestAppStore } from "@/store";
import { NoteEditorTopBarShareButton } from "@/components/note/editor/top-bar/note-editor-top-bar-share-button";
import { NoteEditorTopBarExtraActionsDropdownButton } from "@/components/note/editor/top-bar/note-editor-top-bar-extra-actions-dropdown-button";
import { NoteTopBarTrashedNoteBanner } from "@/components/note/editor/top-bar/note-top-bar-trashed-note-banner";
import { ZIndex } from "@/domains/design/constants";
import { AnyNoteVoiceSuggestion, INoteObservable } from "@/store/note/types";
import { PageElementRouter } from "@/app/outlets/PageElementRouter";
import styled from "@emotion/styled";
import { ItemListRowFeaturedContent } from "@/components/item-list/rows/components/item-list-row-featured-content";
import { sortCollectionsForChips } from "@/domains/collections/sortCollectionsForChips";
import { MDSPanelWidths } from "@/mds/foundations";
import { OrganizeNote, OrganizeNoCollections } from "@/components/note/editor/top-bar/organize";
import { AboutNoteModal } from "@/components/note/editor/top-bar/about/about-note-modal";
import { useState, useCallback, useRef, useEffect } from "react";
import { MDSIconButton, MDSIconButtonSize, MDSIconKind, MDSSpacer, MDSSeparator } from "@/mds";
import { MDSTooltip, MDSTooltipPlacement } from "@/mds/components/tooltip";
import { Popover } from "react-aria-components";
import { MDSText, MDSTextColor, MDSTextVariant } from "@/mds/components/text";
import { NoteObservable } from "@/store/note/NoteObservable";
import { MemCommonEditorStore } from "@/components/note/store/mem-common-editor-store";
import { CleanUpIndicator } from "@/components/note/editor/top-bar/indicators/clean-up-indicator";
import { shouldShowVoiceIndicator } from "@/store/audio/pipeline/audio-pipeline.ui-projection";
import { NoteVersionHistoryMenuBar } from "@/components/note/version-history";
import { useTabPageStore } from "@/components/layout/components/tab-bar/use-tab-page-store";
import { FindInNoteModal } from "@/components/note/editor/top-bar/find-in-note-modal";
import { VoiceIndicator } from "@/components/note/editor/top-bar/indicators/voice-indicator";
import { clientEnvModule } from "@/modules/client-env";
import { getShortcutTooltipConfig } from "@/modules/keyboard-shortcuts/tooltip";

interface NoteEditorTopBarProps {
  editorStore?: MemCommonEditorStore;
  note?: INoteObservable;
  containerWidth: number;
}

export enum NoteSuggestionStrategy {
  VoiceRecording = "voice-recording",
  CleanUp = "clean-up",
}

export const NoteEditorTopBar = observer<NoteEditorTopBarProps>(function NoteEditorTopBar({
  editorStore,
  note,
  containerWidth,
}) {
  return (
    <PageElementRouter
      standardModeElement={
        <StandardNoteComboBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
        />
      }
      guestModeElement={<GuestNoteEditorTopBar note={note} containerWidth={containerWidth} />}
    />
  );
});

const StandardNoteComboBar = observer<NoteEditorTopBarProps>(function StandardNoteComboBar({
  editorStore,
  note,
  containerWidth,
}) {
  const page = useTabPageStore("notesViewPage");

  const isVersionHistoryActive =
    page.noteVersionHistory.currentNoteId && page.noteVersionHistory.currentNoteId === note?.id;

  return (
    <>
      <div style={{ display: isVersionHistoryActive ? "none" : "block" }}>
        <StandardNoteEditorTopBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
        />
      </div>
      <div style={{ display: isVersionHistoryActive ? "block" : "none" }}>
        <StandardNoteVersionHistoryTopBar
          editorStore={editorStore}
          note={note}
          containerWidth={containerWidth}
        />
      </div>
    </>
  );
});

const StandardNoteVersionHistoryTopBar = observer<NoteEditorTopBarProps>(
  function StandardNoteVersionHistoryTopBar({
    editorStore: _editorStore,
    note: _note,
    containerWidth: _containerWidth,
  }) {
    return (
      <>
        <Container $variant="version-history">
          <NoteVersionHistoryMenuBar />
        </Container>
      </>
    );
  }
);

const StandardNoteEditorTopBar = observer<NoteEditorTopBarProps>(function StandardNoteEditorTopBar({
  editorStore,
  note,
  containerWidth,
}) {
  const { store } = useAppStore();
  const page = useTabPageStore("notesViewPage");

  const latestAudioRecordingForNote = store.audioRecordings.getNoteRecording({
    noteId: note?.id ?? null,
  }).recording;
  const activeRecording = store.audio.activeRecording;
  const activeRecordingForNote =
    activeRecording?.associatedNoteId === note?.id ? activeRecording : null;
  const voiceIndicatorRecording = activeRecordingForNote ?? latestAudioRecordingForNote ?? null;

  const [aboutModalOpen, setAboutModalOpen] = useState(false);
  const [organizeIsOpen, setOrganizeIsOpen] = useState(false);
  const [mobileTooltipOpen, setMobileTooltipOpen] = useState(false);
  const microphoneButtonRef = useRef<HTMLButtonElement>(null);

  const toggleOrganizeIsOpen = useCallback(() => {
    setOrganizeIsOpen(!organizeIsOpen);
  }, [organizeIsOpen]);

  const isCollapsed = containerWidth <= MDSPanelWidths.narrow;

  const hasSuggestion = editorStore?.isShowingDifferences;
  const suggestionData = editorStore?.noteObservable?.suggestionData;
  const suggestionKind = suggestionData?.kind;

  const suggestionStrategy = (() => {
    switch (suggestionKind) {
      case "VOICE_SESSION_V1":
      case "VOICE_SESSION_REFINE_V1":
        return NoteSuggestionStrategy.VoiceRecording;
      case "NOTE_CLEANUP_V1":
      case "NOTE_CLEANUP_REFINE_V1":
        return NoteSuggestionStrategy.CleanUp;
      default:
        return null;
    }
  })();

  const noteVoiceSuggestion: AnyNoteVoiceSuggestion | null = (() => {
    if (!note?.id) return null;

    if (suggestionStrategy !== NoteSuggestionStrategy.VoiceRecording) {
      return null;
    }

    if (!suggestionData) {
      return null;
    }

    return {
      noteId: note.id,
      data: suggestionData,
    } as AnyNoteVoiceSuggestion;
  })();

  const isActiveRecordingForNote = Boolean(
    note?.id && store.audio.activeRecordingNoteId === note.id
  );

  const isVoiceIndicatorVisible = (() => {
    if (!note?.id) return false;

    // Show indicator immediately when this note has the active recording,
    // even before the recording observable loads from the lazy store.
    if (isActiveRecordingForNote) return true;

    return shouldShowVoiceIndicator({
      recording: voiceIndicatorRecording ?? null,
      hasVoiceSuggestion: Boolean(noteVoiceSuggestion),
      snapshot: voiceIndicatorRecording
        ? store.audio.pipeline.getSnapshot(voiceIndicatorRecording.id)
        : null,
    });
  })();

  const isCleanUpIndicatorVisible = suggestionStrategy === NoteSuggestionStrategy.CleanUp;

  const isMobileBrowser = clientEnvModule.isMobileBrowser();
  const isMicrophoneDisabled = store.audio.recording.isInitializing;

  // Auto-hide mobile tooltip after 3 seconds
  useEffect(() => {
    if (mobileTooltipOpen && isMobileBrowser) {
      const timer = setTimeout(() => {
        setMobileTooltipOpen(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [mobileTooltipOpen, isMobileBrowser]);

  useEffect(() => {
    if (!page.organizeMenuOpenRequestCount) {
      return;
    }

    setOrganizeIsOpen(true);
    page.consumeOpenOrganizeMenuRequest?.();
  }, [page, page.organizeMenuOpenRequestCount]);

  // Close mobile tooltip when clicking outside
  useEffect(() => {
    if (!mobileTooltipOpen || !isMobileBrowser) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        microphoneButtonRef.current &&
        !microphoneButtonRef.current.contains(event.target as Node)
      ) {
        setMobileTooltipOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [mobileTooltipOpen, isMobileBrowser]);

  if (!note) return null;
  if (note.isTrashed) return <NoteTopBarTrashedNoteBanner note={note} />;

  const collections = note.collectionList?.allCollections || [];

  return (
    <>
      <Container $variant={hasSuggestion ? "suggestion" : "default"}>
        {collections.length === 0 && (
          <OrganizeNoCollections
            note={note}
            onToggleOrganizeIsOpen={toggleOrganizeIsOpen}
            renderTrigger={trigger => (
              <OrganizeNote
                note={note}
                showAutoOrganize
                organizeIsOpen={organizeIsOpen}
                onOrganizeIsOpenChange={setOrganizeIsOpen}
              >
                {trigger}
              </OrganizeNote>
            )}
          />
        )}
        {collections.length > 0 && (
          <OrganizeNote
            note={note}
            showAutoOrganize
            organizeIsOpen={organizeIsOpen}
            onOrganizeIsOpenChange={setOrganizeIsOpen}
          >
            <MDSTooltip
              config={getShortcutTooltipConfig({
                action: "organize_selected_notes",
                label: "Organize",
                placement: MDSTooltipPlacement.Top,
              })}
            >
              <StyledHashtagButton
                iconKind={MDSIconKind.Collection}
                onClick={toggleOrganizeIsOpen}
              />
            </MDSTooltip>
          </OrganizeNote>
        )}
        <ItemListRowFeaturedContent
          compact
          collections={sortCollectionsForChips(collections)}
          noteId={note.id}
          onCollectionOverflowClick={() => setAboutModalOpen(true)}
        />
        <Spacer />
        <>
          {note instanceof NoteObservable && (
            <IconsContainer>
              {isVoiceIndicatorVisible && editorStore && (
                <VoiceIndicator
                  audioRecording={voiceIndicatorRecording}
                  noteSuggestion={noteVoiceSuggestion}
                  editorStore={editorStore}
                  isActiveRecording={isActiveRecordingForNote}
                />
              )}
              {isCleanUpIndicatorVisible && editorStore && (
                <CleanUpIndicator editorStore={editorStore} />
              )}
              {!isVoiceIndicatorVisible && (
                <>
                  {isMobileBrowser ? (
                    <>
                      <StyledMicrophoneButton
                        ref={microphoneButtonRef}
                        size={MDSIconButtonSize.Medium}
                        iconKind={MDSIconKind.Microphone}
                        $isDisabled={isMobileBrowser}
                        aria-disabled={isMobileBrowser}
                        onClick={() => setMobileTooltipOpen(true)}
                      />
                      <Popover
                        triggerRef={microphoneButtonRef}
                        placement="top"
                        isOpen={mobileTooltipOpen}
                        onOpenChange={setMobileTooltipOpen}
                        isNonModal
                        style={{ zIndex: ZIndex.FloatingTooltip }}
                      >
                        <TooltipContent>
                          <MDSText variant={MDSTextVariant.TextXs} color={MDSTextColor.OnAccent}>
                            Voice recording is not available on mobile browsers
                          </MDSText>
                        </TooltipContent>
                      </Popover>
                    </>
                  ) : (
                    <MDSTooltip
                      config={{
                        label: isMicrophoneDisabled
                          ? "Starting voice recording..."
                          : "Start voice recording",
                        placement: MDSTooltipPlacement.Top,
                      }}
                    >
                      <MDSIconButton
                        size={MDSIconButtonSize.Medium}
                        iconKind={MDSIconKind.Microphone}
                        isDisabled={isMicrophoneDisabled}
                        onClick={() => {
                          if (isMicrophoneDisabled) return;
                          void store.audio.createAndStartAudioRecording({
                            associatedNoteId: note.id,
                            calendarEventId: note.associatedSpaceAccountCalendarEventId,
                          });
                        }}
                      />
                    </MDSTooltip>
                  )}
                </>
              )}
              {!isCollapsed && (
                <MDSTooltip
                  config={getShortcutTooltipConfig({
                    action: "cleanup_note",
                    label: "Clean up notes in a single click",
                    placement: MDSTooltipPlacement.Top,
                  })}
                >
                  <MDSIconButton
                    size={MDSIconButtonSize.Medium}
                    iconKind={
                      note.isProcessingSuggestion ? MDSIconKind.Spinner : MDSIconKind.Cleanup
                    }
                    isDisabled={note.isProcessingSuggestion}
                    onClick={() => note.cleanup()}
                  />
                </MDSTooltip>
              )}
              {!isCollapsed && <MDSSeparator orientation="vertical" />}
            </IconsContainer>
          )}
          {!isCollapsed && (
            <MDSTooltip
              config={
                note.isFavorited
                  ? {
                      label: "Unpin from sidebar",
                      placement: MDSTooltipPlacement.Top,
                    }
                  : getShortcutTooltipConfig({
                      action: "pin_note",
                      label: "Pin to sidebar",
                      placement: MDSTooltipPlacement.Top,
                    })
              }
            >
              <MDSIconButton
                size={MDSIconButtonSize.Medium}
                iconKind={note?.isFavorited ? MDSIconKind.ThumbtackSolid : MDSIconKind.Thumbtack}
                onClick={async () => await note?.toggleFavorite()}
                isDisabled={!note}
              />
            </MDSTooltip>
          )}
          <NoteEditorTopBarExtraActionsDropdownButton
            note={note}
            editorStore={editorStore}
            isCollapsed={isCollapsed}
          />
          <FindInNoteModal store={editorStore} />
        </>
      </Container>

      <AboutNoteModal
        isOpen={aboutModalOpen}
        onClose={() => setAboutModalOpen(false)}
        note={note}
      />
    </>
  );
});

const GuestNoteEditorTopBar = observer<NoteEditorTopBarProps>(function GuestNoteEditorTopBar({
  note,
}) {
  const { guestStore } = useGuestAppStore();
  return (
    <div className={topBarStyles}>
      <MDSSpacer />
      <NoteEditorTopBarShareButton note={note} shareSheetModal={guestStore.shareSheetModal} />
    </div>
  );
});

export const NOTE_EDITOR_TOP_BAR_HEIGHT = "40px";

const topBarStyles = css({
  alignItems: "center",
  display: "flex",
  gap: "4px",
  height: NOTE_EDITOR_TOP_BAR_HEIGHT,
  left: 0,
  // Use padding, not margin.
  padding: "6px 16px 0 16px",
  position: "relative",
  top: 0,
  width: "100%",
  zIndex: ZIndex.NavBar, // Above the editor so it scrolls under.

  "@media (max-width: 900px)": {
    padding: "0 8px",
  },
});

const Container = styled.div<{ $variant?: "default" | "suggestion" | "version-history" }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing["2"]} ${theme.spacing["5"]} 0 ${theme.spacing["7"]}`};
  ${({ $variant, theme }) =>
    $variant === "suggestion" && `background-color: ${theme.colors.background.diff};`};
  ${({ $variant, theme }) =>
    $variant === "version-history" &&
    `background-color: ${theme.colors.background.banner.emphasis};`};

  ${({ theme }) => `
    ${theme.mediaQueries.rules.phone} {
      padding-left: ${theme.spacing[4]};
      padding-right: ${theme.spacing[4]};
    }
    `}
`;

const Spacer = styled.div`
  flex: 1;
`;

const IconsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledHashtagButton = styled(MDSIconButton)`
  margin-right: ${({ theme }) => theme.spacing[1]};
`;

const StyledMicrophoneButton = styled(MDSIconButton)<{ $isDisabled: boolean }>`
  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    cursor: not-allowed;
    opacity: 0.4;

    &[data-hovered]:not([data-disabled]) {
      background-color: unset;
      transition: unset;
    }
    `}
`;

const TooltipContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["2"]};
  padding-block: ${({ theme }) => theme.spacing["1"]};
  padding-inline: ${({ theme }) => theme.spacing["2"]};
  background-color: ${({ theme }) => theme.colors.surface.tooltip};
  border-radius: 6px;
  width: fit-content;
`;
